import { useEffect, useState, useCallback } from "react";
import { Wallet, Landmark, FileCheck2, ShieldCheck } from "lucide-react";
import api, { formatApiError, downloadFile } from "@/lib/api";
import { PageHeader } from "@/components/ui-helpers";
import { PayoutHoldBanner } from "@/components/PayoutHoldBanner";
import { MobileVerifyCard } from "@/pages/VerifyMobile";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const label = (s) => (s || "").replace(/_/g, " ");
const bankBadge = (s) => s === "verified" ? "bg-emerald-100 text-emerald-700" : s === "verification_failed" || s === "suspended" ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700";

export default function SellerSettlements() {
  const [fin, setFin] = useState(null);
  const [comm, setComm] = useState({ rules: [] });
  const [bank, setBank] = useState(null);
  const [shipC, setShipC] = useState(null);
  const [form, setForm] = useState({ holder: "", bank: "", account_number: "", ifsc: "" });
  const [mobileReq, setMobileReq] = useState(null);

  const load = useCallback(() => {
    api.get("/marketplace/seller/finance").then((r) => setFin(r.data)).catch(() => setFin({ summary: {}, rows: [], batches: [] }));
    api.get("/marketplace/seller/commission").then((r) => setComm(r.data)).catch(() => {});
    api.get("/marketplace/seller/bank").then((r) => setBank(r.data.bank)).catch(() => {});
    api.get("/marketplace/seller/shipping-charges").then((r) => setShipC(r.data)).catch(() => {});
    api.get("/auth/mobile/requirement").then((r) => setMobileReq(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const accept = async (rule_id) => { try { await api.post("/marketplace/seller/commission/accept", { rule_id }); toast.success("Commission terms accepted"); load(); } catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); } };
  const saveBank = async () => {
    if (!form.holder || !form.account_number || !form.ifsc) { toast.error("Fill all bank fields"); return; }
    try { await api.post("/marketplace/seller/bank", form); toast.success("Bank details submitted for verification"); setForm({ holder: "", bank: "", account_number: "", ifsc: "" }); load(); }
    catch (e) { toast.error(formatApiError(e?.response?.data?.detail)); }
  };

  const mobileBlocked = mobileReq?.applies && !mobileReq?.verified && ["block_action", "hard_block"].includes(mobileReq?.strictness);
  if (mobileBlocked) return (
    <div data-testid="seller-finance-page">
      <PageHeader title="Finance & Settlements" icon={Wallet} subtitle="Verify your mobile number to access payouts and settlement settings." />
      <div className="max-w-lg" data-testid="payout-mobile-gate">
        <p className="mb-2 text-sm font-semibold text-amber-700">Mobile verification is required to view and manage your payouts.</p>
        <MobileVerifyCard onVerified={() => api.get("/auth/mobile/requirement").then((r) => setMobileReq(r.data)).catch(() => {})} />
      </div>
    </div>
  );
  if (!fin) return null;
  const s = fin.summary || {};

  return (
    <div data-testid="seller-finance-page">
      <PageHeader title="Finance & Settlements" icon={Wallet} subtitle="Your marketplace earnings after commission. Settlement is a separate process — nothing is marked paid without a real payout reference." />

      <div className="mb-5"><PayoutHoldBanner /></div>

      <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="finance-summary">
        {[["Gross Sales", s.gross], ["Commission", s.commission], ["GST (incl.)", s.gst_included], ["Refunds", s.refunds], ["Adjustments", s.adjustments], ["Net Payable", s.net_payable], ["Paid", s.paid], ["Pending", s.pending]].map(([k, v]) => (
          <div key={k} className="rounded-2xl border border-slate-200 bg-white p-4"><p className="text-2xl font-bold text-slate-900">₹{v ?? 0}</p><p className="text-xs text-slate-500">{k}</p></div>
        ))}
      </div>

      {shipC && (shipC.total > 0) && (
        <div className="mb-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="seller-shipping-charges">
          <p className="mb-2 text-sm font-semibold text-slate-800">Shipping charges</p>
          <div className="grid grid-cols-3 gap-3 text-sm">
            <div><p className="text-xs text-slate-400">Order freight (from payout)</p><p className="font-bold text-slate-800">₹{shipC.order_freight_total}</p></div>
            <div><p className="text-xs text-slate-400">Manual (prepaid)</p><p className="font-bold text-slate-800">₹{shipC.manual_prepaid_total}</p></div>
            <div><p className="text-xs text-slate-400">Total</p><p className="font-bold text-slate-900">₹{shipC.total}</p></div>
          </div>
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Bank account */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="bank-section">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><Landmark className="h-4 w-4 text-blue-500" /> Settlement Bank Account</div>
          {bank && (
            <div className="mb-3 rounded-xl bg-slate-50 p-3 text-sm">
              <p className="font-medium text-slate-800">{bank.holder} · {bank.bank}</p>
              <p className="text-slate-600">A/C {bank.account_masked} · {bank.ifsc}</p>
              <span className={`mt-1 inline-block rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${bankBadge(bank.status)}`} data-testid="bank-status">{label(bank.status)}</span>
            </div>
          )}
          <div className="grid gap-2 sm:grid-cols-2">
            <Input value={form.holder} onChange={(e) => setForm({ ...form, holder: e.target.value })} placeholder="Account holder name" data-testid="bank-holder" />
            <Input value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} placeholder="Bank name" data-testid="bank-name" />
            <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} placeholder="Account number" data-testid="bank-account" />
            <Input value={form.ifsc} onChange={(e) => setForm({ ...form, ifsc: e.target.value })} placeholder="IFSC" data-testid="bank-ifsc" />
          </div>
          <button onClick={saveBank} data-testid="bank-save" className="mt-3 rounded-full brand-gradient-bg px-5 py-2 text-sm font-semibold text-white">{bank ? "Update & re-verify" : "Submit for verification"}</button>
          <p className="mt-2 text-[11px] text-slate-400">Your account number is masked and stored securely — never shown publicly or to the AI assistant.</p>
        </div>

        {/* Commission acceptance */}
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="commission-section">
          <div className="mb-3 flex items-center gap-2 text-sm font-semibold text-slate-800"><FileCheck2 className="h-4 w-4 text-blue-500" /> Commission Terms</div>
          {(comm.rules || []).map((r) => (
            <div key={r.rule_id} className="flex items-center justify-between border-t border-slate-100 py-2 first:border-t-0">
              <div className="text-sm"><p className="font-medium text-slate-800">{r.name}</p><p className="text-xs text-slate-500 capitalize">{label(r.scope)} · {r.comm_type === "fixed" ? `₹${r.value}` : `${r.value}%`}</p></div>
              {r.accepted ? <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-600"><ShieldCheck className="h-3.5 w-3.5" /> Accepted</span>
                : <button onClick={() => accept(r.rule_id)} data-testid={`accept-${r.rule_id}`} className="rounded-full border border-blue-500 px-3 py-1 text-xs font-semibold text-blue-600">Accept</button>}
            </div>
          ))}
        </div>
      </div>

      {/* Settlement batches */}
      {(fin.batches || []).length > 0 && (
        <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="settlement-batches">
          <p className="mb-2 text-sm font-semibold text-slate-800">Settlement batches</p>
          <div className="divide-y divide-slate-50">
            {fin.batches.map((b) => (
              <div key={b.batch_id} className="flex flex-wrap items-center justify-between gap-2 py-2.5" data-testid={`seller-batch-${b.batch_id}`}>
                <div><p className="text-sm font-semibold text-slate-800">{b.number} <span className="font-normal capitalize text-slate-400">· {label(b.status)}</span></p>
                  <p className="text-xs text-slate-500">Net payable ₹{b.net_payable} · Paid ₹{b.settled_amount || 0}</p></div>
                <button onClick={() => downloadFile(`/marketplace/settlements/${b.batch_id}/statement.pdf`, `${b.number}-statement.pdf`)} data-testid={`seller-statement-${b.batch_id}`} className="rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 hover:bg-slate-50">Statement PDF</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Statement */}
      <div className="mt-5 overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="statement">
        <div className="border-b border-slate-100 p-3 text-sm font-semibold text-slate-800">Statement</div>
        {(fin.rows || []).length === 0 ? <div className="p-8 text-center text-sm text-slate-500">No transactions yet.</div> : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="p-3">Order</th><th className="p-3">Gross</th><th className="p-3">Commission</th><th className="p-3">Payable</th><th className="p-3">Order Status</th><th className="p-3">Settlement</th></tr></thead>
            <tbody>{fin.rows.map((r) => (
              <tr key={r.number} className="border-t border-slate-50" data-testid={`stmt-${r.number}`}>
                <td className="p-3 font-semibold text-slate-800">{r.number}</td><td className="p-3">₹{r.gross}</td>
                <td className="p-3 text-rose-500">− ₹{r.commission}</td><td className="p-3 font-semibold">₹{r.payable}</td>
                <td className="p-3 capitalize">{label(r.status)}</td><td className="p-3 capitalize">{label(r.settlement_state)}</td>
              </tr>))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
